import RouteAbilityGuard from 'core/auth/RouteAbilityGuard';
import RouteAuthGuard from 'core/auth/RouteAuthGuard';
import RouteModuleGuard from 'core/auth/RouteModuleGuard';
import RouteNotAuthGuard from 'core/auth/RouteNotAuthGuard';
import RouteChangeTracker from 'core/components/RouterChangeTracker';
import RootErrorBoundary from 'errors/RootErrorBoundary';
import MainLayout from 'layout';
import { OrderStateEnum } from 'models/enums/orderStateEnum';
import RouteOrderEditPageTabGuard from 'pages/OrderPage/route/RouteOrderEditPageTabGuard';
import PayoutsPage from 'pages/PayoutsPage/PayoutsPage';
import SignIn from 'pages/SignIn';
import { createBrowserRouter, Navigate } from 'react-router-dom';

import MangoCallsPage from 'pages/MangoCallsPage/MangoCallsPage';
import CalendarPage from 'pages/CalendarPage/CalendarPage';
import UsersPage from 'pages/UsersPage/UsersPage';
import UserPage from 'pages/UserPage/UserPage';
import UserEditPage from 'pages/UserPage/UserEditPage';
import UserViewPage from 'pages/UserPage/UserViewPage';
import ClientsPage from 'pages/ClientsPage/ClientsPage';
import ClientPage from 'pages/ClientPage/ClientPage';
import ClientEditPage from 'pages/ClientPage/ClientEditPage';
import ClientViewPage from 'pages/ClientPage/ClientViewPage';
import ProductsPage from 'pages/ProductsPage/ProductsPage';
import RequestCreatePage from 'pages/OrderPage/RequestCreatePage';
import OrdersPage from 'pages/OrdersPage/OrdersPage';
import OrderPage from 'pages/OrderPage/OrderPage';
import OrderEditPage from 'pages/OrderPage/OrderEditPage';
import AnalyticsPage from 'pages/AnalyticsPage/AnalyticsPage';
import TagsPage from 'pages/Settings/Tags/TagsPage';
import ProfilePage from 'pages/ProfilePage/ProfilePage';
import ProfileViewPage from 'pages/ProfilePage/ProfileViewPage';
import ProfileEditPage from 'pages/ProfilePage/ProfileEditPage';
import SettingsPage from 'pages/Settings/SettingsPage/SettingsPage';

const routes = createBrowserRouter([
  {
    path: '/',
    element: (
      <>
        <RouteChangeTracker />
        <MainLayout />
      </>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      {
        errorElement: <RootErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to='/analytics' state={{ replace: true }} />,
          },
          {
            path: '/analytics',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability='Analytics' fallback='/calendar'>
                  <AnalyticsPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/calendar',
            element: (
              <RouteAuthGuard>
                <CalendarPage />
              </RouteAuthGuard>
            ),
          },
          {
            path: '/requests',
            element: (
              <RouteAuthGuard>
                <OrdersPage ordersType={OrderStateEnum.Request} />
              </RouteAuthGuard>
            ),
          },
          {
            path: '/requests/new',
            element: (
              <RouteAuthGuard>
                <RequestCreatePage />
              </RouteAuthGuard>
            ),
          },
          {
            path: '/requests/:orderId',
            element: (
              <RouteAuthGuard>
                <OrderPage />
              </RouteAuthGuard>
            ),
            children: [
              {
                path: '/requests/:orderId/:tab?',
                element: (
                  <RouteOrderEditPageTabGuard>
                    <OrderEditPage />
                  </RouteOrderEditPageTabGuard>
                ),
              },
            ],
          },
          ...(
            [
              ['/orders', OrderStateEnum.Order],
              ['/archive', OrderStateEnum.Archive],
            ] satisfies Array<[string, OrderStateEnum]>
          )
            .map(([p, s]) => [
              {
                path: p,
                element: (
                  <RouteAuthGuard>
                    <OrdersPage ordersType={s} />
                  </RouteAuthGuard>
                ),
              },
              {
                path: `${p}/:orderId`,
                element: (
                  <RouteAuthGuard>
                    <OrderPage />
                  </RouteAuthGuard>
                ),
                children: [
                  {
                    path: `${p}/:orderId/:tab?`,
                    element: (
                      <RouteOrderEditPageTabGuard>
                        <OrderEditPage />
                      </RouteOrderEditPageTabGuard>
                    ),
                  },
                ],
              },
            ])
            .flat(),
          {
            path: '/users',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Users'}>
                  <UsersPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/users/:userId',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Users'}>
                  <UserPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
            children: [
              {
                path: '/users/:userId',
                element: <UserViewPage />,
              },
              {
                path: '/users/:userId/edit',
                element: <UserEditPage />,
              },
            ],
          },
          {
            path: '/clients',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Clients'}>
                  <ClientsPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/clients/:clientId',
            element: (
              <RouteAuthGuard>
                <ClientPage />
              </RouteAuthGuard>
            ),
            children: [
              {
                path: '/clients/:clientId',
                element: <ClientViewPage />,
              },
              {
                path: '/clients/:clientId/edit',
                element: <ClientEditPage />,
              },
            ],
          },
          {
            path: '/products',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Products'}>
                  <ProductsPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/payouts',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Payouts'}>
                  <RouteModuleGuard module='payouts'>
                    <PayoutsPage />
                  </RouteModuleGuard>
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/mango-calls',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'MangoCalls'}>
                  <MangoCallsPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
          {
            path: '/profile',
            element: (
              <RouteAuthGuard>
                <ProfilePage />
              </RouteAuthGuard>
            ),
            children: [
              {
                path: '/profile',
                element: <ProfileViewPage />,
              },
              {
                path: '/profile/edit',
                element: <ProfileEditPage />,
              },
            ],
          },
          {
            path: '/settings',
            element: (
              <RouteAuthGuard>
                <SettingsPage />
              </RouteAuthGuard>
            ),
          },
          {
            path: '/settings/tags',
            element: (
              <RouteAuthGuard>
                <RouteAbilityGuard ability={'Tags'}>
                  <TagsPage />
                </RouteAbilityGuard>
              </RouteAuthGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: (
      <RouteNotAuthGuard>
        <SignIn />
      </RouteNotAuthGuard>
    ),
  },
]);

export default routes;
